import {
  Stack, Text, Group, Badge, Progress, Button, Divider, Paper,
} from '@mantine/core'
import { IconCheck, IconX, IconMinus, IconClock } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { api } from '@/api/client'

interface Approval {
  approver_user_id: number
  approver_name: string
  approver_role: string
  decision: string | null
  decided_at: string | null
}

interface GmudCabPanelProps {
  gmudId: number
  tenantId: number | string | undefined
  /**
   * Status da GMUD. Entra na decisão do botão porque `submit_cab_vote` recusa
   * voto fora de `gmud_pending_cab` (400 `gmud_nao_esta_em_votacao_cab`) — e o
   * caso é de rotina: o quórum é menor que o número de membros, então quando a
   * GMUD avança quem não votou ainda estava vendo Aprovar/Rejeitar/Abster.
   */
  status: string
  approvals: Approval[]
  quorumRequired: number
  currentUserId: number
  canVote: boolean
}

function decisionIcon(decision: string | null) {
  if (decision === 'approved') return <IconCheck size={12} color="green" />
  if (decision === 'rejected') return <IconX size={12} color="red" />
  if (decision === 'abstained') return <IconMinus size={12} color="gray" />
  return <IconClock size={12} color="orange" />
}

function decisionColor(decision: string | null): string {
  if (decision === 'approved') return 'green'
  if (decision === 'rejected') return 'red'
  if (decision === 'abstained') return 'gray'
  return 'orange'
}

export function GmudCabPanel({ gmudId, tenantId, status, approvals, quorumRequired, currentUserId, canVote }: GmudCabPanelProps) {
  const queryClient = useQueryClient()
  const approved = approvals.filter((a) => a.decision === 'approved').length
  const myApproval = approvals.find((a) => a.approver_user_id === currentUserId)
  const hasVoted = !!myApproval?.decision
  const emVotacao = status === 'gmud_pending_cab'

  const mutation = useMutation({
    mutationFn: async (decision: string) => {
      await api.post(`/v1/tenants/${tenantId}/gmud/${gmudId}/vote`, { decision })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmud', tenantId, String(gmudId)] })
      notifications.show({ color: 'green', message: 'Voto registrado.' })
    },
    onError: (err: Error) => {
      notifications.show({ color: 'red', message: `Erro: ${err.message}` })
    },
  })

  const pct = Math.min((approved / quorumRequired) * 100, 100)

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="sm">Votação do CCM</Text>
        <Text size="xs" c="dimmed">{approved}/{quorumRequired} aprovações para avançar</Text>
      </Group>

      <Progress value={pct} color={pct >= 100 ? 'green' : 'blue'} size="sm" radius="xl" />

      <Stack gap="xs">
        {approvals.map((a) => (
          <Group key={a.approver_user_id} justify="space-between">
            <Group gap="xs">
              {decisionIcon(a.decision)}
              <Text size="sm">{a.approver_name}</Text>
              <Badge size="xs" variant="dot" color="gray">{a.approver_role}</Badge>
            </Group>
            <Badge size="xs" color={decisionColor(a.decision)} variant="light">
              {a.decision ?? 'Pendente'}
            </Badge>
          </Group>
        ))}
      </Stack>

      {canVote && myApproval && !hasVoted && emVotacao && (
        <>
          <Divider />
          <Text size="xs" fw={500} c="dimmed">Seu voto (irreversível após confirmação)</Text>
          <Group>
            <Button
              size="xs"
              color="green"
              leftSection={<IconCheck size={12} />}
              onClick={() => mutation.mutate('approved')}
              loading={mutation.isPending}
            >
              Aprovar
            </Button>
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={<IconX size={12} />}
              onClick={() => mutation.mutate('rejected')}
              loading={mutation.isPending}
            >
              Rejeitar
            </Button>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconMinus size={12} />}
              onClick={() => mutation.mutate('abstained')}
              loading={mutation.isPending}
            >
              Abster
            </Button>
          </Group>
        </>
      )}

      {/* Sem isto o botão simplesmente desapareceria para o membro que não
          votou — dizer por quê evita o suporte "perdi meu voto". */}
      {canVote && myApproval && !hasVoted && !emVotacao && (
        <Text size="xs" c="dimmed">
          Votação encerrada — esta GMUD já saiu da fase de votação do CCM.
        </Text>
      )}

      {hasVoted && (
        <Text size="xs" c={decisionColor(myApproval?.decision ?? null)}>
          Você votou: {myApproval?.decision}
        </Text>
      )}
    </Stack>
  )
}
